import React from "react";
import { Helmet } from "react-helmet-async";
import { BIZ, SOCIAL_LINKS } from "../lib/constants";

const SITE_URL = BIZ.siteUrl;
const DEFAULT_DESC =
    "Liquor Studio — Clinton, MO's premier liquor store. Curated bourbon, wine & craft beer in Henry County. Visit 309 S 2nd St or shop online.";

/**
 * Universal SEO wrapper. Sets title (≤60 chars after suffix), description,
 * canonical, Open Graph, Twitter, geo, robots, theme-color, and optional JSON-LD.
 *
 * Pass `type="article"` for blog posts and provide `article` props.
 * Pass `noindex` to suppress indexing (used on 404, admin, etc.).
 * Pass `jsonLd` as a single object or an array of objects — all are emitted.
 */
export const SEO = ({
    title,
    description,
    path = "/",
    image,
    imageAlt,
    type = "website",
    jsonLd = null,
    noindex = false,
    keywords,
    article = null, // { publishedTime, modifiedTime, author, section, tags:[] }
}) => {
    const baseTitle = "Liquor Studio · Clinton MO";
    const fullTitle = title ? `${title} | ${baseTitle}` : baseTitle;
    const desc = description || DEFAULT_DESC;
    const url = `${SITE_URL}${path}`;
    const ogImage = image || `${SITE_URL}/api/static/uploads/store/og-storefront.jpg`;
    const ogAlt = imageAlt || "Liquor Studio storefront and bottle selection in Clinton, Missouri";
    const kw =
        keywords ||
        "Liquor Studio, liquor store Clinton MO, bourbon Clinton Missouri, Henry County liquor, Truman Lake liquor store, wine Clinton MO, craft beer";

    const ldList = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : jsonLd ? [jsonLd] : [];

    return (
        <Helmet prioritizeSeoTags>
            <html lang="en" />
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <meta name="keywords" content={kw} />
            <meta name="author" content={BIZ.name} />
            <link rel="canonical" href={url} />

            <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1"} />

            {/* Theme + locale */}
            <meta name="theme-color" content="#0A0A0A" />
            <meta name="color-scheme" content="dark" />

            {/* Geo (local SEO) */}
            <meta name="geo.region" content="US-MO" />
            <meta name="geo.placename" content="Clinton" />
            <meta name="geo.position" content="38.369;-93.782" />
            <meta name="ICBM" content="38.369, -93.782" />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={BIZ.name} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:alt" content={ogAlt} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:locale" content="en_US" />

            {/* Article specifics */}
            {type === "article" && article?.publishedTime && (
                <meta property="article:published_time" content={article.publishedTime} />
            )}
            {type === "article" && article?.modifiedTime && (
                <meta property="article:modified_time" content={article.modifiedTime} />
            )}
            {type === "article" && article?.author && (
                <meta property="article:author" content={article.author} />
            )}
            {type === "article" && article?.section && (
                <meta property="article:section" content={article.section} />
            )}
            {type === "article" &&
                (article?.tags || []).map((t) => (
                    <meta key={t} property="article:tag" content={t} />
                ))}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@LiquorStudioMO" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content={ogAlt} />

            {/* DNS prefetch / preconnect for off-domain resources */}
            <link rel="dns-prefetch" href="https://shop.liquor.studio" />
            <link rel="preconnect" href="https://shop.liquor.studio" crossOrigin="anonymous" />

            {/* sameAs hints (Twitter site already in card; LD sameAs handles the rest) */}
            {SOCIAL_LINKS.map((s) => (
                <link key={s.key} rel="me" href={s.url} />
            ))}

            {ldList.map((ld, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(ld)}
                </script>
            ))}
        </Helmet>
    );
};

export default SEO;
